import React, { useMemo, useRef, useEffect, useState } from "react";

function getHostEl() {
  return (
    document.querySelector("#nexus-search-widget") ||
    document.querySelector(".nexus-search-widget") ||
    document.querySelector("[data-nexus-search]")
  );
}

// Convert Font Awesome 6 syntax to Font Awesome 5 (for compatibility)
function normalizeFAFamily(family) {
  if (family === "fa-solid") return "fas";
  if (family === "fa-regular") return "far";
  if (family === "fa-brands") return "fab";
  // If already FA5 format, return as is
  if (family === "fas" || family === "far" || family === "fab") return family;
  // Default to solid
  return "fas";
}

function readGuestIconSpec() {
  const el = getHostEl();
  if (!el) {
    return { mode: "fa", faFamily: "fas", faIcon: "fa-user", size: 16, color: "#0f172a", svg: "", show: true };
  }
  const mode = (el.getAttribute("data-guest-icon-mode") || "fa").toLowerCase() === "svg" ? "svg" : "fa";
  const faFamilyRaw = el.getAttribute("data-guest-icon-family") || "fa-solid";
  const faFamily = normalizeFAFamily(faFamilyRaw);
  const faIcon = el.getAttribute("data-guest-icon-name") || "fa-user";
  const size = parseInt(el.getAttribute("data-guest-icon-size") || "16", 10) || 16;
  const color = el.getAttribute("data-guest-icon-color") || "#0f172a";
  const svg = el.getAttribute("data-guest-icon-svg") || "";
  const show = el.getAttribute("data-guest-icon-show") !== "false";
  return { mode, faFamily, faIcon, size, color, svg, show };
}

function GuestsIcon() {
  const [spec, setSpec] = useState(readGuestIconSpec);

  useEffect(() => {
    const el = getHostEl();
    if (!el) return;
    const sync = () => setSpec(readGuestIconSpec());
    const mo = new MutationObserver((muts) => {
      for (const m of muts) {
        if (m.type !== "attributes") continue;
        const n = m.attributeName || "";
        if (
          n === "data-guest-icon-mode" ||
          n === "data-guest-icon-family" ||
          n === "data-guest-icon-name" ||
          n === "data-guest-icon-size" ||
          n === "data-guest-icon-color" ||
          n === "data-guest-icon-svg" ||
          n === "data-guest-icon-show"
        ) {
          sync();
        }
      }
    });
    mo.observe(el, { attributes: true });
    return () => mo.disconnect();
  }, []);

  if (!spec.show) return null;

  if (spec.mode === "svg" && spec.svg) {
    return (
      <span
        aria-hidden="true"
        style={{ display: "inline-grid", width: spec.size, height: spec.size, color: spec.color, placeItems: "center" }}
        dangerouslySetInnerHTML={{ __html: spec.svg }}
      />
    );
  }

  return (
    <i
      aria-hidden="true"
      className={`${spec.faFamily} ${spec.faIcon}`}
      style={{ fontSize: spec.size, color: spec.color, lineHeight: 1 }}
    />
  );
}

export default function GuestsDropdown({
  palette,
  maxGuests = 16,
  adults,
  children,
  infants,
  pets,
  setAdults,
  setChildren,
  setInfants,
  setPets,
  fullWidth = true,
}) {
  const [open, setOpen] = useState(false);
  const dropRef = useRef(null);

  // 🔹 Local temp state for Apply/Cancel flow
  const [tAdults, setTAdults] = useState(adults);
  const [tChildren, setTChildren] = useState(children);
  const [tInfants, setTInfants] = useState(infants);
  const [tPets, setTPets] = useState(pets);

  // when opening, reset temps from props
  useEffect(() => {
    if (open) {
      setTAdults(adults);
      setTChildren(children);
      setTInfants(infants);
      setTPets(pets);
    }
  }, [open, adults, children, infants, pets]);

  const guests = Math.max(1, tAdults + tChildren);
  const totalNonInfants = tAdults + tChildren;
  const canIncAdults = totalNonInfants < maxGuests;
  const canIncChildren = totalNonInfants < maxGuests;

  const guestsLabel = useMemo(() => {
    const el = document.querySelector("#nexus-search-widget") || document.querySelector(".nexus-search-widget") || document.querySelector("[data-nexus-search]");
    const singularLabel = el?.getAttribute("data-guests-label-singular") || "Guest";
    const pluralLabel = el?.getAttribute("data-guests-label-plural") || "Guests";

    const count = Math.max(1, adults + children);
    const parts = [`${count} ${count === 1 ? singularLabel : pluralLabel}`];
    if (infants > 0) parts.push(`${infants} ${infants === 1 ? "Infant" : "Infants"}`);
    if (pets > 0) parts.push(`${pets} ${pets === 1 ? "Pet" : "Pets"}`);
    return parts.join(", ");
  }, [adults, children, infants, pets]);

  // Handle ESC key to close modal
  useEffect(() => {
    const onEsc = (e) => {
      if (e.key === "Escape" && open) {
        setOpen(false);
      }
    };
    document.addEventListener("keydown", onEsc);
    return () => document.removeEventListener("keydown", onEsc);
  }, [open]);

  // ====== STYLES via CSS vars ======
  const rootStyle = {
    position: "relative",
    justifySelf: "end",
    width: fullWidth ? "100%" : "auto",
  };

  const pill = {
    display: "flex",
    alignItems: "center",
    gap: "var(--sw-pill-gap, 10px)",
    border: `1px solid var(--sw-pill-bc, ${palette.border})`,
    borderRadius: "var(--sw-pill-r, 10px)",
    padding: "var(--sw-pill-py, 10px) var(--sw-pill-px, 12px)",
    background: "var(--sw-pill-bg, transparent)",
    cursor: "pointer",
    userSelect: "none",
    justifyContent: "space-between",
    width: fullWidth ? "100%" : "auto",
    minWidth: "var(--sw-pill-minw, 150px)",
    height: "var(--sw-pill-h, 44px)",
    fontFamily: "var(--sw-pill-ff, inherit)",
    fontSize: "var(--sw-pill-fs, 14px)",
    fontWeight: "var(--sw-pill-fw, 600)",
    color: "var(--sw-pill-fc, inherit)",
    boxSizing: "border-box",
  };

  // Modal overlay
  const overlay = {
    position: "fixed",
    inset: 0,
    background: "var(--swm-overlay, rgba(0,0,0,.35))",
    zIndex: 10000,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    padding: 16,
  };

  // Modal panel
  const modalPanel = {
    background: "var(--swm-bg, #ffffff)",
    width: "var(--swm-w, 520px)",
    maxWidth: "95vw",
    maxHeight: "90vh",
    borderRadius: "var(--swm-r, 16px)",
    boxShadow: "var(--swm-shadow, 0 8px 24px rgba(2,6,23,.12))",
    border: "1px solid var(--swm-bc, rgba(15,23,42,.2))",
    display: "flex",
    flexDirection: "column",
    overflow: "hidden",
  };

  const modalHeader = {
    padding: "var(--swm-pad, 16px)",
    borderBottom: "1px solid var(--swm-div, rgba(148,163,184,.2))",
    fontFamily: "var(--swm-title-ff, inherit)",
    fontSize: "var(--swm-title-fs, 20px)",
    fontWeight: "var(--swm-title-fw, 800)",
    color: "var(--swm-title-fc, #0f172a)",
  };

  const modalBody = {
    padding: "var(--swm-pad, 16px)",
    overflowY: "auto",
    flex: 1,
  };

  const rowBase = {
    display: "grid",
    gridTemplateColumns: "1fr auto auto auto",
    gap: "var(--sw-row-gap, 8px)",
    alignItems: "center",
    padding: "var(--sw-row-py, 10px) 0",
    borderBottom: "1px solid var(--sw-row-bc, rgba(148,163,184,.2))",
  };
  const rowLast = { ...rowBase, borderBottom: "none" };

  const label = {
    display: "flex",
    flexDirection: "column",
    fontFamily: "var(--sw-label-ff, inherit)",
    fontWeight: "var(--sw-label-fw, 700)",
    fontSize: "var(--sw-label-fs, 14px)",
    color: "var(--sw-label-fc, #0f172a)",
  };
  const sub = {
    fontFamily: "var(--sw-sub-ff, inherit)",
    fontWeight: "var(--sw-sub-fw, 400)",
    fontSize: "var(--sw-sub-fs, 12px)",
    color: "var(--sw-sub-fc, #0f172aB3)",
    marginTop: 2,
  };
  const valueStyle = {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    textAlign: "center",
    minWidth: "var(--sw-value-minw, 20px)",
    fontFamily: "var(--sw-value-ff, inherit)",
    fontWeight: "var(--sw-value-fw, 700)",
    fontSize: "var(--sw-value-fs, 14px)",
    color: "var(--sw-value-fc, #0f172a)",
  };

  // Center the +/- buttons with flex
  const ctr = (disabled) => ({
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    width: "var(--sw-ctr-size, 32px)",
    height: "var(--sw-ctr-size, 32px)",
    borderRadius: "var(--sw-ctr-radius, 8px)",
    border: `1px solid var(--sw-ctr-bc, ${palette.border})`,
    background: disabled ? "var(--sw-ctr-bg-disabled, rgba(127,127,127,.2))" : "var(--sw-ctr-bg, #ffffff)",
    color: "var(--sw-ctr-fc, #0f172a)",
    cursor: disabled ? "not-allowed" : "pointer",
    fontWeight: 700,
  });

  const notice = {
    fontFamily: "var(--sw-note-ff, inherit)",
    fontWeight: "var(--sw-note-fw, 400)",
    fontSize: "var(--sw-note-fs, 12px)",
    color: "var(--sw-note-fc, #0f172aBF)",
    marginTop: "var(--sw-note-mt, 8px)",
  };

  const modalFooter = {
    padding: "var(--swm-pad, 16px)",
    borderTop: "1px solid var(--swm-div, rgba(148,163,184,.2))",
    display: "grid",
    gridTemplateColumns: "1fr 1fr",
    gap: 12,
  };
  const cancelBtn = {
    width: "100%",
    borderRadius: "var(--sw-act-close-r, 10px)",
    border: `1px solid var(--sw-act-close-bc, ${palette.border})`,
    background: "var(--sw-act-close-bg, transparent)",
    color: "var(--sw-act-close-fc, inherit)",
    padding: "var(--sw-act-close-py, 10px) var(--sw-act-close-px, 12px)",
    cursor: "pointer",
    fontFamily: "var(--sw-act-close-ff, inherit)",
    fontWeight: "var(--sw-act-close-fw, 600)",
    fontSize: "var(--sw-act-close-fs, 14px)",
  };
  const applyBtn = {
    width: "100%",
    borderRadius: "var(--sw-act-apply-r, 10px)",
    border: `1px solid var(--sw-act-apply-bc, ${palette.border})`,
    background: "var(--sw-act-apply-bg, #f38b4a)",
    color: "var(--sw-act-apply-fc, #fff)",
    padding: "var(--sw-act-apply-py, 10px) var(--sw-act-apply-px, 12px)",
    cursor: "pointer",
    fontFamily: "var(--sw-act-apply-ff, inherit)",
    fontWeight: "var(--sw-act-apply-fw, 700)",
    fontSize: "var(--sw-act-apply-fs, 14px)",
  };
  // handlers (operate on temp only)
  const decAdults = () => setTAdults((n) => Math.max(1, n - 1));
  const incAdults = () => canIncAdults && setTAdults((n) => n + 1);
  const decChildren = () => setTChildren((n) => Math.max(0, n - 1));
  const incChildren = () => canIncChildren && setTChildren((n) => n + 1);
  const decInfants = () => setTInfants((n) => Math.max(0, n - 1));
  const incInfants = () => setTInfants((n) => n + 1);
  const decPets = () => setTPets((n) => Math.max(0, n - 1));
  const incPets = () => setTPets((n) => n + 1);

  const onApply = () => {
    setAdults(tAdults);
    setChildren(tChildren);
    setInfants(tInfants);
    setPets(tPets);
    setOpen(false);
  };
  const onCancel = () => setOpen(false);

  return (
    <>
      <div style={rootStyle} data-button-type="pill">
        <div style={pill} onClick={() => setOpen((v) => !v)}>
          <span style={{ display: "inline-flex", alignItems: "center", gap: "var(--sw-pill-gap, 8px)" }}>
            <span aria-hidden="true">
              <GuestsIcon />
            </span>
            <span>{guestsLabel}</span>
          </span>
          <span aria-hidden="true">▾</span>
        </div>
      </div>

      {open && (
        <div style={overlay} onClick={onCancel}>
          <style>{`
            .nexus-guests-modal .nxsw-cancel-btn:hover {
              background: var(--swm-cancel-hover-bg, rgba(15,23,42,.05)) !important;
              color: var(--swm-cancel-hover-fc, #0f172a) !important;
              border-color: var(--swm-cancel-hover-bc, rgba(15,23,42,.3)) !important;
            }
            .nexus-guests-modal .nxsw-apply-btn:hover {
              background: var(--swm-apply-hover-bg, #ea580c) !important;
              color: var(--swm-apply-hover-fc, #ffffff) !important;
              border-color: var(--swm-apply-hover-bc, transparent) !important;
            }
            .nexus-guests-modal .nxsw-ctr-btn:hover:not(:disabled) {
              background: var(--swm-ctr-hover-bg, rgba(15,23,42,.05)) !important;
              border-color: var(--swm-ctr-hover-bc, rgba(15,23,42,.3)) !important;
              color: var(--swm-ctr-hover-fc, #0f172a) !important;
            }
          `}</style>
          <div ref={dropRef} className="nexus-guests-modal" style={modalPanel} onClick={(e) => e.stopPropagation()} role="dialog" aria-label="Guest selector">
            {/* Modal Header */}
            <div style={modalHeader}>
              Select Guests
            </div>

            {/* Modal Body */}
            <div style={modalBody}>
              <div style={rowBase}>
                <div style={label}>
                  <strong>Adults</strong>
                  <span style={sub}>Age 13+</span>
                </div>
                <button type="button" className="nxsw-ctr-btn" style={ctr(tAdults <= 1)} onClick={decAdults} disabled={tAdults <= 1}>−</button>
                <div aria-live="polite" style={valueStyle}>{tAdults}</div>
                <button type="button" className="nxsw-ctr-btn" style={ctr(!canIncAdults)} onClick={incAdults} disabled={!canIncAdults}>+</button>
              </div>

              <div style={rowBase}>
                <div style={label}>
                  <strong>Children</strong>
                  <span style={sub}>Ages 2–12</span>
                </div>
                <button type="button" className="nxsw-ctr-btn" style={ctr(tChildren <= 0)} onClick={decChildren} disabled={tChildren <= 0}>−</button>
                <div aria-live="polite" style={valueStyle}>{tChildren}</div>
                <button type="button" className="nxsw-ctr-btn" style={ctr(!canIncChildren)} onClick={incChildren} disabled={!canIncChildren}>+</button>
              </div>

              <div style={rowBase}>
                <div style={label}>
                  <strong>Infants</strong>
                  <span style={sub}>Under 2</span>
                </div>
                <button type="button" className="nxsw-ctr-btn" style={ctr(tInfants <= 0)} onClick={decInfants} disabled={tInfants <= 0}>−</button>
                <div aria-live="polite" style={valueStyle}>{tInfants}</div>
                <button type="button" className="nxsw-ctr-btn" style={ctr(false)} onClick={incInfants}>+</button>
              </div>

              <div style={rowLast}>
                <div style={label}>
                  <strong>Pets</strong>
                  <span style={sub}>Bringing a service animal?</span>
                </div>
                <button type="button" className="nxsw-ctr-btn" style={ctr(tPets <= 0)} onClick={decPets} disabled={tPets <= 0}>−</button>
                <div aria-live="polite" style={valueStyle}>{tPets}</div>
                <button type="button" className="nxsw-ctr-btn" style={ctr(false)} onClick={incPets}>+</button>
              </div>

              {guests > maxGuests && (
                <div style={notice}>
                  Maximum {maxGuests} guests (excluding infants).
                </div>
              )}
            </div>

            {/* Modal Footer */}
            <div style={modalFooter}>
              <button type="button" className="nxsw-cancel-btn" style={cancelBtn} onClick={onCancel}>Cancel</button>
              <button type="button" className="nxsw-apply-btn" style={applyBtn} onClick={onApply}>Apply</button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
